import { useEffect, useState } from "react";

export const getFileSizeByHeadRequest = async (url: string,
  unit: "bytes" | "kb" | "mb" = "bytes"): Promise<number | null> => {
  const controller = new AbortController();
  const signal = controller.signal;

  try {
    const response = await fetch(url, {
      method: "GET",
      signal,
      credentials: "include",
    });

    const contentLength = response.headers.get("content-length");
    if (contentLength) {
      controller.abort();
      const sizeInBytes = parseInt(contentLength, 10);
      if (!Number.isFinite(sizeInBytes)) return null;

      switch (unit) {
        case "kb":
          return Math.round(sizeInBytes / 1024);
        case "mb":
          return +(sizeInBytes / (1024 * 1024)).toFixed(2);
        case "bytes":
        default:
          return sizeInBytes;
      }
    }

    return null;
  } catch {
    return null;
  }
};

const fileSizeCache = new Map<string, number>();

export const useFileSize = (url: string) => {
  const [ fileSize, setFileSize ] = useState<number | null>(null);

  useEffect(() => {
    if (!url) return;

    if (fileSizeCache.has(url)) {
      setFileSize(fileSizeCache.get(url)!);
      return;
    }

    let isCancelled = false;

    getFileSizeByHeadRequest(url)
      .then((size) => {
        if (!isCancelled && typeof size === "number") {
          fileSizeCache.set(url, size);
          setFileSize(size);
        }
      })
      .catch(() => {
        if (!isCancelled) {
          setFileSize(null);
        }
      });

    return () => {
      isCancelled = true;
    };
  }, [ url ]);

  return fileSize ?? 0;
};
